import { Injectable, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

/** Die einzige Route, die ein angemeldetes Terminal sehen darf. */
export const TERMINAL_HOME = '/terminal-dashboard';

/**
 * Hält ein angemeldetes Terminal auf dem Terminal-Dashboard.
 *
 * Bis zum 23.09.2026 gab es keine Sperre dieser Art. `AuthGuard` prüft nur `permittedRoles`,
 * und wo die fehlen, lässt er JEDE Rolle durch. Mit einem Terminal-Token waren deshalb per
 * Adresszeile, Deep-Link oder Zurück erreichbar: `/tasks/**`, `/profile`, `/user-settings`
 * (inklusive Konto löschen), `/registered-stepper` — und `/forbidden` mit seinem
 * `mailto:`-Knopf, der auf Android die App verlässt. Die Rückleitung in `app.component` griff
 * nur einmal pro Sitzung.
 *
 * Eine Sperre über Router-Ereignisse statt eines Guards an jeder Route: Sie deckt auch Routen
 * ab, die erst noch entstehen. Ein Guard, den man an einer neuen Route vergisst, meldet nichts.
 *
 * Geprüft wird das TOKEN, nicht `AuthService.isTerminal` — das Flag wird an mehreren Stellen
 * von Hand gesetzt und steht beim Kaltstart kurz auf false.
 */
@Injectable({ providedIn: 'root' })
export class TerminalRouteLockService implements OnDestroy {
  private sub: Subscription | null = null;

  constructor(private router: Router) {}

  start(): void {
    if (this.sub) return;
    this.sub = this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe((e) => {
        if (istTerminalAngemeldet() && !istErlaubt(e.url)) {
          // Eine Navigation während NavigationStart bricht die laufende ab.
          void this.router.navigateByUrl(TERMINAL_HOME, { replaceUrl: true });
        }
      });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}

/**
 * Erlaubt ist nur das Dashboard selbst (mit Query/Fragment) und `/login`: Dorthin führt das
 * bestätigte Abmelden — zu dem Zeitpunkt ist das Token schon gelöscht, aber die Navigation
 * kann starten, bevor das geschehen ist. Die Login-Seite springt bei gültigem Terminal-Token
 * ohnehin zurück aufs Dashboard.
 *
 * ⚠️ Exakter Pfad, kein `startsWith`: `/terminal-dashboard/pairing` rendert die
 * Kopplungsseite — ein Neukoppeln ohne Admin-PIN.
 */
export function istErlaubt(url: string): boolean {
  const pfad = url.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  return pfad === TERMINAL_HOME || pfad === '/login';
}

export function istTerminalAngemeldet(): boolean {
  const token = localStorage.getItem('token');
  if (!token) return false;
  try {
    const payload = JSON.parse(window.atob(token.split('.')[1]));
    return String(payload?.role ?? '').toUpperCase() === 'TERMINAL';
  } catch {
    return false;
  }
}
